import { useState, useEffect } from 'react';
import type { RoleDefinition } from '../../../app/roles';
import { useRepositories } from '../../../local/provider';
import { PhoneIdentity } from './PhoneIdentity';
import { PhoneTabs } from './PhoneTabs';
import type { AssetJoined } from '../inventory/AssetInventory';
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from '../../../presentation';
import './phone-detail.css';

const phoneDepthContent = defineDepthContent({
  overview: {
    summary: 'Confirm the device owner, service state, location, and next safe lifecycle action.',
    title: 'Person and device outcome',
    signals: ['Current assignee and department', 'Device condition and availability', 'Next lifecycle action'] as const,
  },
  operations: {
    summary: 'Review assignment, compliance, service, maintenance, history, and authorized device actions.',
    title: 'Phone operations',
    signals: ['Assignment and service state', 'Compliance and maintenance', 'Role-aware lifecycle controls'] as const,
  },
  architecture: {
    summary: 'Trace permitted device identifiers through assignment, person, department, service, and audit relationships.',
    title: 'Device relationship graph',
    signals: ['Asset and assignment IDs', 'Person and department links', 'Role-masked technical identifiers'] as const,
  },
});

export function PhoneDetail({ assetId, role, onBack }: { assetId: string, role: RoleDefinition, onBack: () => void }) {
  const repo = useRepositories();
  const { depth } = useDepth();
  const lens = useDepthValue(phoneDepthContent);
  const [asset, setAsset] = useState<AssetJoined | null>(null);

  useEffect(() => {
    async function load() {
      const a = await repo.inventory.getAsset(assetId);
      if (!a) return;

      let assigneeName, departmentName;
      const assignments = await repo.inventory.getAssignmentsByAsset(a.id);
      if (assignments[0]) {
        const person = await repo.campus.getPerson(assignments[0].personId);
        if (person) {
          assigneeName = `${person.firstName} ${person.lastName}`;
          if (person.departmentId) {
            const dept = await repo.campus.getDepartment(person.departmentId);
            departmentName = dept?.name;
          }
        }
      }
      setAsset({ ...a, assigneeName, departmentName });
    }
    load();
  }, [assetId, repo]);

  if (!asset) return <div className="loading" style={{ padding: '2rem' }}>Loading asset details...</div>;

  return (
    <div className="phone-detail-screen">
      <header className="detail-header">
        <button className="button" onClick={onBack} type="button">&larr; Back to Inventory</button>
        <h1>Device Detail: {asset.assetTag}</h1>
      </header>

      <DepthBrief summary={lens.summary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="phone-detail" />

      {depth === 'overview' && (
        <section className="phone-perspective-strip phone-perspective-strip--overview" aria-label="Device outcome">
          <span><strong>Assigned to</strong>{asset.assigneeName || 'No active custodian'}</span>
          <span><strong>Service state</strong>Ready and compliant</span>
          <span><strong>Next action</strong>Verify assignment and warranty</span>
        </section>
      )}
      {depth === 'architecture' && (
        <section className="phone-perspective-strip phone-perspective-strip--architecture" aria-label="Device relationship path">
          <span><strong>Asset</strong>{asset.id}</span>
          <span aria-hidden="true">→</span>
          <span><strong>Assignment</strong>asset.id → assignment.assetId</span>
          <span aria-hidden="true">→</span>
          <span><strong>Person / department</strong>{asset.assigneeName || 'Unassigned'} / {asset.departmentName || 'No department'}</span>
        </section>
      )}

      <div className="phone-detail-layout">
        <aside className="phone-identity-column">
          <PhoneIdentity asset={asset} role={role} />
        </aside>
        <main className="phone-tabs-column">
          <PhoneTabs asset={asset} role={role} depth={depth} />
        </main>
      </div>
    </div>
  );
}
